"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

export interface User {
  id: string;
  email: string;
  has_templates: boolean;
  survey_completed: boolean;
  token: string;
  tokenExpiry: number; // Unix timestamp when token expires
  has_company: boolean;
  ghl_post_list_url: string;
  role?: string;
  is_admin?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (
    id: string,
    email: string,
    has_templates: boolean,
    survey_completed: boolean,
    token: string,
    has_company: boolean,
    ghl_post_list_url: string,
    role?: string,
    is_admin?: boolean
  ) => boolean;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  isLoading: boolean;
  showWelcomeModal: boolean;
  setShowWelcomeModal: (show: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Utility function to check if token is expired
const isTokenExpired = (tokenExpiry: number): boolean => {
  const currentTime = Date.now();
  return currentTime >= tokenExpiry;
};

// Utility function to calculate token expiry (12 hours from now)
const calculateTokenExpiry = (): number => {
  const currentTime = Date.now();
  const twelveHoursInMs = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
  return currentTime + twelveHoursInMs;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);

        // Check if token has expired
        if (userData.tokenExpiry && isTokenExpired(userData.tokenExpiry)) {
          // Token is expired, clear the session
          localStorage.removeItem("user");
          setUser(null);
        } else {
          setUser(userData);
        }
      } catch {
        // Invalid JSON in localStorage, clear it
        localStorage.removeItem("user");
        setUser(null);
      }
    }
    setIsLoading(false);
  }, []);

  // Periodic check for token expiration
  useEffect(() => {
    if (!user) return;

    const checkTokenExpiry = () => {
      if (user.tokenExpiry && isTokenExpired(user.tokenExpiry)) {
        // Token has expired, logout user
        logout();
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Set up interval to check every minute
    const interval = setInterval(checkTokenExpiry, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [user]);

  const login = (
    id: string,
    email: string,
    has_templates: boolean,
    survey_completed: boolean,
    token: string,
    has_company: boolean,
    ghl_post_list_url: string,
    role?: string,
    is_admin?: boolean
  ): boolean => {
    const userData = {
      id: id,
      email: email,
      has_templates: has_templates,
      survey_completed: survey_completed,
      token: token,
      tokenExpiry: calculateTokenExpiry(),
      has_company: has_company,
      ghl_post_list_url: ghl_post_list_url,
      role: role,
      is_admin: is_admin,
    };

    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
    // Show welcome modal after login/register
    setShowWelcomeModal(true);
    return true;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        updateUser,
        isLoading,
        showWelcomeModal,
        setShowWelcomeModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
